import axios from 'axios';
import type { AuthTokenService } from './AuthTokenService';

export interface PhoneLookupRequest {
  msisdn: string;
}

export interface PhoneLookupResponse {
  exists: boolean;
  message: string;
}

export interface OTPRequestParams {
  msisdn: string;
  purpose: 'SIGNUP' | 'LOGIN';
}

export interface OTPRequestResponse {
  request_id: string;
  message: string;
  expires_in: number;
}

export interface OTPRegisterRequest {
  msisdn: string;
  otp_code: string;
  first_name: string;
  last_name: string;
  email?: string;
  marketing_consent?: boolean;
  device_info: {
    device_id: string;
    platform: string;
    user_agent?: string;
  };
}

export interface OTPLoginRequest {
  msisdn: string;
  otp_code: string;
  device_info: {
    device_id: string;
    platform: string;
    user_agent?: string;
  };
}

export interface OTPAuthResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  user: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  registration_status: string;
  authentication_method: string;
}

export interface OTPVerifyResponse {
  valid: boolean;
  message: string;
  request_id: string;
}

export class OTPAuthService {
  private readonly apiClient;

  constructor(
    private readonly authTokenService: AuthTokenService,
    private readonly baseUrl: string
  ) {
    // Use proxy in development to avoid CORS issues

    this.apiClient = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token when available
    this.apiClient.interceptors.request.use(
      (config) => {
        const url = typeof config.url === 'string' ? config.url : '';
        const isAuthEndpoint = url.startsWith('/v1/auth/');
        if (!isAuthEndpoint) {
          const token = this.authTokenService.getAuthToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor to handle token refresh
    this.apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const url = typeof originalRequest?.url === 'string' ? originalRequest.url : '';
        const shouldAttemptRefresh = url !== '' && !url.startsWith('/v1/auth/');
        if (error.response?.status === 401 && shouldAttemptRefresh && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const newToken = await this.authTokenService.refreshToken();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.apiClient(originalRequest);
          } catch (refreshError) {
            this.authTokenService.clearAuthToken();
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async lookupPhone(request: PhoneLookupRequest): Promise<PhoneLookupResponse> {
    const response = await this.apiClient.post('/v1/auth/phone/lookup', {
      msisdn: request.msisdn,
    });

    return {
      exists: response.data.exists,
      message: response.data.message,
    };
  }

  async requestOTP(params: OTPRequestParams): Promise<OTPRequestResponse> {
    const response = await this.apiClient.post('/v1/auth/otp/request', {
      msisdn: params.msisdn,
      purpose: params.purpose,
    });

    return {
      request_id: response.data.request_id,
      message: response.data.message,
      expires_in: response.data.expires_in,
    };
  }

  async registerWithOTP(request: OTPRegisterRequest): Promise<OTPAuthResponse> {
    const response = await this.apiClient.post('/v1/auth/register', {
      msisdn: request.msisdn,
      otp_code: request.otp_code,
      first_name: request.first_name,
      last_name: request.last_name,
      email: request.email,
      marketing_consent: request.marketing_consent,
      device_info: request.device_info,
    });

    const authResponse: OTPAuthResponse = {
      access_token: response.data.access_token,
      refresh_token: response.data.refresh_token,
      expires_in: response.data.expires_in,
      token_type: response.data.token_type,
      user: response.data.user,
      registration_status: response.data.registration_status,
      authentication_method: 'OTP',
    };

    // Store tokens in auth service
    this.authTokenService.setAuthToken(authResponse.access_token);

    return authResponse;
  }

  async loginWithOTP(request: OTPLoginRequest): Promise<OTPAuthResponse> {
    const response = await this.apiClient.post('/v1/auth/login', {
      msisdn: request.msisdn,
      otp_code: request.otp_code,
      device_info: request.device_info,
    });

    const authResponse: OTPAuthResponse = {
      access_token: response.data.access_token,
      refresh_token: response.data.refresh_token,
      expires_in: response.data.expires_in,
      token_type: response.data.token_type,
      user: response.data.user,
      registration_status: response.data.registration_status,
      authentication_method: 'OTP',
    };

    // Store tokens in auth service
    this.authTokenService.setAuthToken(authResponse.access_token);

    return authResponse;
  }

  async validateOTP(msisdn: string, otp: string): Promise<OTPVerifyResponse> {
    const response = await this.apiClient.post('/v1/auth/otp/verify', {
      msisdn: msisdn,
      otp_code: otp,
    });

    return {
      message: response.data.message,
      valid: response.data.valid,
      request_id: response.data.request_id,
    };
  }

  validatePhoneNumber(msisdn: string): {
    isValid: boolean;
    formattedNumber: string;
    error?: string;
  } {
    // Remove all non-digit characters
    const cleaned = msisdn.replace(/\D/g, '');

    // Ghana phone number validation
    if (cleaned.startsWith('233')) {
      // International format (233XXXXXXXXX)
      if (cleaned.length !== 12) {
        return {
          isValid: false,
          formattedNumber: msisdn,
          error:
            'Ghana international number should be 12 digits (233XXXXXXXXX)',
        };
      }
      return {
        isValid: true,
        formattedNumber: `+${cleaned}`,
      };
    } else if (cleaned.startsWith('0')) {
      // Local format (0XXXXXXXXX)
      if (cleaned.length !== 10) {
        return {
          isValid: false,
          formattedNumber: msisdn,
          error: 'Ghana local number should be 10 digits (0XXXXXXXXX)',
        };
      }
      return {
        isValid: true,
        formattedNumber: `+233${cleaned.substring(1)}`,
      };
    } else if (cleaned.length === 9) {
      // Without country code or leading zero (XXXXXXXXX)
      return {
        isValid: true,
        formattedNumber: `+233${cleaned}`,
      };
    }

    return {
      isValid: false,
      formattedNumber: msisdn,
      error:
        'Invalid phone number format. Please use Ghana format (e.g., 0241234567 or +233241234567)',
    };
  }

  formatPhoneForDisplay(msisdn: string): string {
    const validation = this.validatePhoneNumber(msisdn);
    if (!validation.isValid) {
      return msisdn;
    }

    const cleaned = validation.formattedNumber.replace('+233', '');
    // Format as 024 123 4567
    return `024 ${cleaned.substring(0, 3)} ${cleaned.substring(3)}`;
  }

  generateDeviceInfo(): any {
    return {
      device_id: this.generateDeviceId(),
      platform: this.detectPlatform(),
      user_agent:
        typeof window !== 'undefined' ? window.navigator.userAgent : 'Server',
    };
  }

  // Helper methods for OTP UI
  formatOTPInput(input: string): string {
    // Only allow digits and limit to 6 characters
    return input.replace(/\D/g, '').substring(0, 6);
  }

  isValidOTP(otp: string): boolean {
    return /^\d{6}$/.test(otp);
  }

  getOTPMask(length: number = 6): string {
    return '•'.repeat(length);
  }

  // Rate limiting helpers
  canRequestOTP(msisdn: string): { canRequest: boolean; waitTime?: number } {
    if (typeof window === 'undefined') return { canRequest: true };

    const storageKey = `otp_request_${msisdn}`;
    const lastRequestStr = localStorage.getItem(storageKey);

    if (!lastRequestStr) {
      return { canRequest: true };
    }

    const lastRequest = parseInt(lastRequestStr, 10);
    const now = Date.now();
    const timeDiff = now - lastRequest;
    const waitTime = 60000; // 1 minute

    if (timeDiff < waitTime) {
      return {
        canRequest: false,
        waitTime: Math.ceil((waitTime - timeDiff) / 1000),
      };
    }

    return { canRequest: true };
  }

  recordOTPRequest(msisdn: string): void {
    if (typeof window !== 'undefined') {
      const storageKey = `otp_request_${msisdn}`;
      localStorage.setItem(storageKey, Date.now().toString());
    }
  }

  private generateDeviceId(): string {
    // Try to get existing device ID from localStorage
    if (typeof window !== 'undefined') {
      let deviceId = localStorage.getItem('motito_device_id');
      if (!deviceId) {
        deviceId = `device_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`;
        localStorage.setItem('motito_device_id', deviceId);
      }
      return deviceId;
    }
    return `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private detectPlatform(): string {
    if (typeof window === 'undefined') return 'server';

    const userAgent = window.navigator.userAgent.toLowerCase();
    if (userAgent.includes('mobile')) return 'mobile';
    if (userAgent.includes('tablet')) return 'tablet';
    return 'web';
  }
}
